import { createCoachingServices } from "./coaching-services";
import { resolveMockZ6Route } from "./coaching-mock-z6";
import { mockToolViewDiagnostics } from "./coaching-mock-tool-view";
import { resolveMockPlatformOrganizationRoute } from "./coaching-mock-platform-organizations";
import { resolveMockAnalyticsRoute } from "./coaching-mock-analytics";
import { evaluateDeniedMutationAttempt, type DeniedMutationRole } from "../domain/denied-actions";
import type { ApiHttpMethod } from "./api-contracts";
import { createMockAuthStore } from "./coaching-mock-auth";
import { createMockArtifactLifecycleStore } from "./coaching-mock-artifact-lifecycle";
import { createMockCohortLifecycleStore } from "./coaching-mock-cohort-lifecycle";
import { createMockOrganizationLifecycleStore } from "./coaching-mock-organization-lifecycle";
import { createMockSessionLifecycleStore } from "./coaching-mock-session-lifecycle";
import { createMockUserLifecycleStore } from "./coaching-mock-user-lifecycle";
import { list, pageParticipants, pageSessions } from "./coaching-mock-list-paging";
import type {
  CoachingArtifactDto,
  CoachingCohortDto,
  CoachingCompanyDto,
  CoachingHistoryEventDto,
  CoachingOperationResult,
  CoachingParticipantDto,
  CoachingProgramDto,
  CoachingResourceDto,
  CoachingSessionDto
} from "./coaching-dtos";
type MockRequestOptions = { method?: string; body?: BodyInit | null; idempotencyKey?: string };
type MockBlobResponse = { blob: Blob; filename: string | null; contentType: string | null };

const companies: CoachingCompanyDto[] = [{ id: "company-1", name: "Acme Leadership", status: "active" }];
const programs: CoachingProgramDto[] = [{ id: "program-1", name: "Executive Coaching", company_id: "company-1", coach_id: "coach-1" }];
const cohorts: CoachingCohortDto[] = [
  { id: "cohort-1", name: "Spring Leaders", company_id: "company-1", program_id: "program-1", status: "active" }
];
const participants: CoachingParticipantDto[] = [
  { id: "participant-1", email: "learner@example.com", name: "Learner One", cohort_id: "cohort-1", status: "active" }
];
const sessions: CoachingSessionDto[] = [
  {
    id: "session-1",
    cohort_id: "cohort-1",
    title: "Session 1",
    starts_at: "2099-03-08T14:00:00-05:00",
    timezone: "America/New_York",
    invite_status: "provider_closed",
    participant_count: 1,
    coach_id: "coach-1",
    status: "scheduled"
  }
];
// Phase 4: seed ONE row of EACH report_type on a shared session so the D-3
// leak specs have confidential rows to prove are filtered out. The backend G3
// predicate is the source of truth; the FE list mapper + visibility evaluator
// drop the non-readable types belt-and-suspenders.
const reports: CoachingArtifactDto[] = [
  {
    id: "report-1",
    session_id: "session-1",
    title: "Session 1 Team Report",
    status: "ready",
    report_type: "team_admin",
    artifact_visibility: "company_readable",
    download_policy: "company_readable",
    delivery_mode: "in_dashboard",
    summary: "The team made steady progress on delegation and feedback cadence this session.",
    highlights: ["Delegation framework adopted by 4 of 5 managers", "Weekly 1:1 cadence now consistent"],
    action_items: ["Schedule peer-shadowing for the two newest managers", "Review delegation outcomes at session 2"],
    cohort_name: "Spring Leaders",
    session_title: "Session 1",
    reviewed_at: null,
    reviewed_by: null
  },
  {
    id: "report-coach-1",
    session_id: "session-1",
    title: "Session 1 Coach Feedback",
    status: "ready",
    report_type: "coach_feedback",
    artifact_visibility: "coach_only",
    delivery_mode: "in_dashboard",
    summary: "Coach-private feedback on facilitation and participant dynamics.",
    cohort_name: "Spring Leaders",
    session_title: "Session 1"
  },
  {
    id: "report-internal-1",
    session_id: "session-1",
    title: "Session 1 VJAL Internal Review",
    status: "ready",
    report_type: "vjal_internal",
    artifact_visibility: "staff_only",
    delivery_mode: "in_dashboard",
    summary: "VJAL-internal account-health and renewal-signal notes.",
    cohort_name: "Spring Leaders",
    session_title: "Session 1"
  },
  {
    id: "report-ceo-1",
    session_id: "session-1",
    title: "Session 1 CEO Confidential",
    status: "ready",
    report_type: "ceo_confidential",
    artifact_visibility: "staff_only",
    delivery_mode: "ceo_out_of_band",
    summary: "Confidential CEO briefing delivered out-of-band.",
    cohort_name: "Spring Leaders",
    session_title: "Session 1"
  }
];
const resources: CoachingResourceDto[] = [
  {
    id: "resource-1",
    title: "Coaching primer",
    status: "published",
    upload_id: "upload-1",
    download_status: "ready",
    resource_type: "documentation",
    description: "Onboarding handbook for new coaches.",
    url: "https://resources.example/primer",
    is_public: true
  },
  {
    id: "resource-2",
    title: "Session 1 walkthrough",
    status: "published",
    upload_id: "upload-2",
    download_status: "ready",
    resource_type: "reference_video",
    description: "Recorded reference walkthrough — viewable, not downloadable by coaches.",
    url: "https://resources.example/session-1-video",
    is_public: true
  },
  {
    id: "resource-3",
    title: "Delegation worksheet",
    status: "published",
    upload_id: "upload-3",
    download_status: "ready",
    resource_type: "program_resource",
    description: "Program worksheet attached to the Spring Leaders cohort.",
    url: "https://resources.example/delegation",
    is_public: false,
    cohort_name: "Spring Leaders"
  }
];
// Wave 10: the mock event-history fixture carries the same taxonomy `category`
// the live read stamps on each event, so the distinct-log VIEWS (invite / report
// / participant / session / access) exercise their category filters in the mock
// e2e lane. These mirror real coaching event actions; nothing is fabricated
// beyond seeding the in-memory store the contract fixtures already use.
const history: CoachingHistoryEventDto[] = [
  {
    id: "event-1",
    action: "coaching_company_created",
    category: "foundation",
    actor: "Operator",
    actor_id: "mock-admin-1",
    summary: "Company Acme created",
    occurred_at: "2026-06-20T10:00:00.000Z"
  },
  {
    id: "event-2",
    action: "invite_send_requested",
    category: "integration",
    actor: "Coach Lee",
    actor_id: "mock-coach-1",
    summary: "Invites requested for Session 1",
    occurred_at: "2026-06-20T11:00:00.000Z"
  },
  {
    id: "event-3",
    action: "coaching_report_reviewed",
    category: "report",
    actor: "Operator",
    actor_id: "mock-admin-1",
    summary: "Team report reviewed",
    occurred_at: "2026-06-20T12:00:00.000Z"
  },
  {
    id: "event-4",
    action: "coaching_participant_created",
    category: "participant",
    actor: "Coach Lee",
    actor_id: "mock-coach-1",
    summary: "Participant added to Spring Leaders",
    occurred_at: "2026-06-20T13:00:00.000Z"
  },
  {
    id: "event-5",
    action: "coaching_session_created",
    category: "session",
    actor: "Coach Lee",
    actor_id: "mock-coach-1",
    summary: "Session 2 scheduled",
    occurred_at: "2026-06-20T14:00:00.000Z"
  },
  {
    id: "event-6",
    action: "coaching_mutation_denied",
    category: "permission",
    actor: "Company Admin",
    actor_id: "mock-company-1",
    summary: "Blocked: company admin attempted a write",
    occurred_at: "2026-06-20T15:00:00.000Z"
  }
];
const authStore = createMockAuthStore();
const userLifecycleStore = createMockUserLifecycleStore(
  [
    {
      id: "user-1",
      email: "coach@example.com",
      name: "Coach One",
      role: "coach",
      company_id: "company-1",
      program_id: "program-1",
      cohort_id: "cohort-1",
      scope_label: "Acme Leadership / Spring Leaders"
    }
  ],
  appendHistoryOnce,
  () => authStore.getProfile().role
);
const organizationLifecycleStore = createMockOrganizationLifecycleStore({
  companies,
  programs,
  cohorts,
  sessions,
  appendHistory: appendHistoryOnce
});
const cohortLifecycleStore = createMockCohortLifecycleStore({ cohorts, participants, sessions, appendHistory: appendHistoryOnce });
const sessionLifecycleStore = createMockSessionLifecycleStore({ cohorts, participants, sessions, appendHistory: appendHistoryOnce });
const artifactLifecycleStore = createMockArtifactLifecycleStore({ reports, resources, appendHistory: appendHistoryOnce });

export const mockCoachingApi = {
  async request<T>(path: string, options: MockRequestOptions = {}): Promise<T> {
    const { pathname, searchParams } = new URL(path, "http://mock.local");
    const method = options.method ?? "GET";
    const authResult = authStore.handle(pathname, options);
    const platformOrganizationResult = resolveMockPlatformOrganizationRoute(pathname, method, options.body);
    if (platformOrganizationResult !== null) return platformOrganizationResult as T;
    if (authResult.handled) return authResult.value as T;

    if (method !== "GET") {
      const body = parseBody<Record<string, unknown>>(options.body);
      const denied = evaluateMockDeniedAttempt(method, pathname, body, options.idempotencyKey);
      if (denied) {
        appendHistoryOnce({
          id: denied.event.id,
          action: denied.event.action,
          actor_id: denied.event.actorId,
          summary: denied.event.summary
        });
        return {
          ok: false,
          status: denied.status,
          reference: denied.reference,
          side_effects: ["mutation.denied", "history-append"]
        } as T;
      }
      const z6Post = resolveMockZ6Route(pathname, method);
      if (z6Post !== null) return z6Post as T;
      if (pathname === "/users" && method === "POST") return userLifecycleStore.create(body, options.idempotencyKey) as T;
      if (pathname === "/companies" && method === "POST") {
        return organizationLifecycleStore.createCompany(body, options.idempotencyKey) as T;
      }
      if (pathname.startsWith("/companies/") && method === "PATCH") {
        return organizationLifecycleStore.updateCompany(recordIdFromPath(pathname), body, options.idempotencyKey) as T;
      }
      if (pathname === "/programs" && method === "POST") {
        return organizationLifecycleStore.createProgram(body, options.idempotencyKey) as T;
      }
      if (pathname.startsWith("/programs/") && method === "PATCH") {
        return organizationLifecycleStore.updateProgram(recordIdFromPath(pathname), body, options.idempotencyKey) as T;
      }
      if (pathname.startsWith("/users/") && pathname.endsWith("/deactivate") && method === "POST") {
        return userLifecycleStore.deactivate(recordIdFromPath(pathname), options.idempotencyKey) as T;
      }
      if (pathname.startsWith("/users/") && method === "PATCH") {
        return userLifecycleStore.update(recordIdFromPath(pathname), body, options.idempotencyKey) as T;
      }
      if (pathname === "/cohorts" && method === "POST") {
        return cohortLifecycleStore.createCohort(body, options.idempotencyKey) as T;
      }
      if (pathname.startsWith("/cohorts/") && pathname.endsWith("/complete") && method === "POST") {
        return cohortLifecycleStore.completeCohort(recordIdFromPath(pathname), options.idempotencyKey) as T;
      }
      if (pathname.startsWith("/cohorts/") && pathname.endsWith("/sessions/series/preview") && method === "POST") {
        return sessionLifecycleStore.previewSeries(recordIdFromPath(pathname), body, options.idempotencyKey) as T;
      }
      if (pathname.startsWith("/cohorts/") && pathname.endsWith("/sessions/series") && method === "POST") {
        return sessionLifecycleStore.createSeries(recordIdFromPath(pathname), body, options.idempotencyKey) as T;
      }
      if (pathname.startsWith("/cohorts/") && pathname.endsWith("/sessions/reschedule-future") && method === "POST") {
        return sessionLifecycleStore.rescheduleFuture(recordIdFromPath(pathname), body, options.idempotencyKey) as T;
      }
      if (pathname === "/participants" && method === "POST") {
        return cohortLifecycleStore.createParticipant(body, options.idempotencyKey) as T;
      }
      if (
        pathname.startsWith("/cohorts/") &&
        pathname.endsWith("/participants") &&
        !pathname.includes("/participants/") &&
        method === "POST"
      ) {
        return cohortLifecycleStore.attachParticipantToCohort(recordIdFromPath(pathname), body, options.idempotencyKey) as T;
      }

      if (pathname.startsWith("/cohorts/") && pathname.endsWith("/participants/bulk") && method === "POST") {
        return cohortLifecycleStore.bulkImportParticipants(recordIdFromPath(pathname), body, options.idempotencyKey) as T;
      }
      if (pathname.startsWith("/cohorts/") && pathname.includes("/participants/") && method === "DELETE") {
        // F6 remove-from-cohort: /cohorts/:cohort_id/participants/:participant_id.
        const parts = pathname.split("/").filter(Boolean);
        return cohortLifecycleStore.removeParticipant(parts[1] ?? "", parts[3] ?? "", options.idempotencyKey) as T;
      }
      if (pathname.startsWith("/cohorts/") && pathname.includes("/participants/") && method === "PATCH") {
        // Cohort-scoped participant patch: keep it out of the cohort store and
        // answer with the patched participant snapshot.
        const participantId = pathname.split("/").filter(Boolean)[3] ?? "";
        const participant = participants.find((item) => item.id === participantId) ?? participants[0];
        return { ...participant, ...body, id: participant.id } as T;
      }
      if (pathname.startsWith("/cohorts/") && method === "PATCH") {
        return cohortLifecycleStore.updateCohort(recordIdFromPath(pathname), body, options.idempotencyKey) as T;
      }
      if (pathname.startsWith("/sessions/") && pathname.endsWith("/send-invites") && method === "POST") {
        return sessionLifecycleStore.sendInvites(recordIdFromPath(pathname), body, options.idempotencyKey) as T;
      }
      if (pathname.startsWith("/sessions/") && pathname.endsWith("/force-resend") && method === "POST") {
        return sessionLifecycleStore.forceResend(recordIdFromPath(pathname), body, options.idempotencyKey) as T;
      }
      if (pathname.startsWith("/sessions/") && pathname.endsWith("/reschedule") && method === "POST") {
        return sessionLifecycleStore.rescheduleOne(recordIdFromPath(pathname), body, options.idempotencyKey) as T;
      }
      if (pathname.startsWith("/sessions/") && pathname.endsWith("/sync-zoom") && method === "POST") {
        return sessionLifecycleStore.syncZoom(recordIdFromPath(pathname), options.idempotencyKey) as T;
      }
      if (pathname.startsWith("/sessions/") && pathname.endsWith("/reassign-coach") && method === "POST") {
        return sessionLifecycleStore.reassignCoach(recordIdFromPath(pathname), body, options.idempotencyKey) as T;
      }
      if (pathname.startsWith("/sessions/") && pathname.endsWith("/lifecycle") && method === "POST") {
        return sessionLifecycleStore.lifecycle(recordIdFromPath(pathname), body, options.idempotencyKey) as T;
      }
      if (pathname === "/resources" && method === "POST") {
        return artifactLifecycleStore.createResource(body, options.idempotencyKey) as T;
      }
      if (pathname.startsWith("/resources/") && pathname.includes("/uploads/") && pathname.endsWith("/complete") && method === "POST") {
        return artifactLifecycleStore.completeUpload(recordIdFromPath(pathname), uploadIdFromPath(pathname), body, options.idempotencyKey) as T;
      }
      if (pathname.startsWith("/resources/") && pathname.includes("/uploads/") && pathname.endsWith("/download") && method === "POST") {
        return artifactLifecycleStore.requestDownload(recordIdFromPath(pathname), uploadIdFromPath(pathname), options.idempotencyKey) as T;
      }
      if (pathname.startsWith("/resources/") && pathname.includes("/uploads/") && method === "DELETE") {
        return artifactLifecycleStore.deleteUpload(recordIdFromPath(pathname), uploadIdFromPath(pathname), options.idempotencyKey) as T;
      }
      if (pathname.startsWith("/resources/") && method === "PATCH") {
        return artifactLifecycleStore.updateResource(recordIdFromPath(pathname), body, options.idempotencyKey) as T;
      }
      if (pathname.startsWith("/reports/") && pathname.endsWith("/review") && method === "POST") {
        return artifactLifecycleStore.reviewReport(recordIdFromPath(pathname), body, options.idempotencyKey) as T;
      }
      return mockOperation(pathname, options.idempotencyKey) as T;
    }

    const z6Get = resolveMockZ6Route(pathname, method);
    if (z6Get !== null) return z6Get as T;
    const analyticsGet = resolveMockAnalyticsRoute(pathname);
    if (pathname === "/tools/diagnostics") return mockToolViewDiagnostics() as T;
    if (analyticsGet !== null) return analyticsGet as T;
    if (pathname.startsWith("/exports/")) {
      // CSV export mock acknowledgement (the live lane returns text/csv via the
      // api-client text path). The signed-url stays redacted; no raw payload.
      return {
        ok: true,
        export_type: pathname.split("/").filter(Boolean)[1] ?? "reports",
        signed_url_redacted: true,
        status: "export_ready"
      } as T;
    }
    if (pathname.startsWith("/companies/")) return organizationLifecycleStore.findCompany(recordIdFromPath(pathname)) as T;
    if (pathname.startsWith("/programs/")) return organizationLifecycleStore.findProgram(recordIdFromPath(pathname)) as T;
    if (pathname.startsWith("/cohorts/") && pathname.endsWith("/progress")) return mockOperation(pathname) as T;
    if (pathname.startsWith("/cohorts/") && pathname.endsWith("/report")) return mockOperation(pathname) as T;
    // Cohort-scoped roster ONLY — the leading /cohorts/ guard stops this from
    // shadowing the bare GET /participants (the admin roster) handled below.
    if (pathname.startsWith("/cohorts/") && pathname.endsWith("/participants")) {
      return list(cohortLifecycleStore.listParticipantsForCohort(recordIdFromPath(pathname))) as T;
    }
    if (pathname.startsWith("/cohorts/") && pathname.endsWith("/sessions")) {
      return list(sessionLifecycleStore.listSessionsForCohort(recordIdFromPath(pathname))) as T;
    }
    if (pathname.startsWith("/cohorts/")) return cohortLifecycleStore.findCohort(recordIdFromPath(pathname)) as T;
    if (pathname.startsWith("/participants/")) return participants[0] as T;
    if (pathname.startsWith("/sessions/") && pathname.endsWith("/transcript")) {
      return artifactLifecycleStore.sessionTranscript(recordIdFromPath(pathname)) as T;
    }
    if (pathname.startsWith("/sessions/") && pathname.endsWith("/report")) return artifactLifecycleStore.sessionReport(recordIdFromPath(pathname)) as T;
    if (pathname.startsWith("/sessions/")) return sessionLifecycleStore.findSession(recordIdFromPath(pathname)) as T;
    if (pathname.startsWith("/reports/")) return artifactLifecycleStore.findReport(recordIdFromPath(pathname)) as T;
    if (pathname.startsWith("/resources/")) return artifactLifecycleStore.findResource(recordIdFromPath(pathname)) as T;
    if (pathname.startsWith("/event-history/")) return history[0] as T;
    if (pathname.startsWith("/users/") && pathname.endsWith("/zoom-status")) return mockOperation(pathname) as T;
    if (pathname.startsWith("/users/")) return userLifecycleStore.find(recordIdFromPath(pathname)) as T;

    if (pathname === "/companies") {
      return list(organizationLifecycleStore.listCompanies(searchParams)) as T;
    }
    if (pathname === "/programs") {
      return list(organizationLifecycleStore.listPrograms(searchParams)) as T;
    }
    if (pathname === "/cohorts") return list(cohortLifecycleStore.listCohorts()) as T;
    if (pathname === "/participants") {
      return pageParticipants(participants, searchParams) as T;
    }
    if (pathname === "/sessions") {
      return pageSessions(sessions, searchParams) as T;
    }
    if (pathname === "/reports") return list(artifactLifecycleStore.listReports(searchParams)) as T;
    if (pathname === "/resources") return list(artifactLifecycleStore.listResources(searchParams)) as T;
    if (pathname === "/event-history") return list(history) as T;
    if (pathname === "/users") return userLifecycleStore.list(searchParams) as T;

    return list([]) as T;
  },
  async requestBlob(path: string): Promise<MockBlobResponse> {
    const { pathname, searchParams } = new URL(path, "http://mock.local");
    if (!pathname.startsWith("/sessions/") || !pathname.endsWith("/report/download")) {
      throw new Error(`Mock blob route not found: ${pathname}`);
    }
    const sessionId = recordIdFromPath(pathname);
    const reportId = searchParams.get("reportId");
    const report = reportId
      ? reports.find((item) => item.session_id === sessionId && item.id === reportId)
      : reports.find((item) => item.session_id === sessionId && item.report_type === "team_admin");
    if (!report) throw new Error("Mock report not found");
    const html = `<!doctype html><html><body><h1>${report.title ?? "Coaching report"}</h1><p>${report.summary ?? ""}</p></body></html>`;
    return {
      blob: new Blob([html], { type: "text/html; charset=utf-8" }),
      filename: `coaching-report-${report.id}.html`,
      contentType: "text/html; charset=utf-8"
    };
  },
};

function appendHistoryOnce(event: CoachingHistoryEventDto) {
  if (history.some((item) => item.id === event.id)) return;
  history.push(event);
}

function mockOperation(pathname: string, idempotencyKey?: string): CoachingOperationResult {
  return {
    ok: true,
    reference: idempotencyKey ?? `mock:${pathname}`,
    status: "mocked",
    side_effects: ["db1-write", "readiness-evidence"]
  };
}

function evaluateMockDeniedAttempt(
  method: string,
  pathname: string,
  body: Record<string, unknown>,
  idempotencyKey?: string
) {
  const role = body.actor_role;
  if (role !== "company_admin" && role !== "participant") return null;

  return evaluateDeniedMutationAttempt({
    role: role as DeniedMutationRole,
    method: method as ApiHttpMethod,
    pathname,
    payload: body,
    actorId: typeof body.actor_id === "string" ? body.actor_id : role,
    supportRef: idempotencyKey
  });
}

function parseBody<T>(body: BodyInit | null | undefined): T {
  if (typeof body === "string" && body.trim()) return JSON.parse(body) as T;
  return {} as T;
}

function recordIdFromPath(pathname: string) {
  return pathname.split("/").filter(Boolean)[1] ?? "";
}

function uploadIdFromPath(pathname: string) {
  const segments = pathname.split("/").filter(Boolean);
  const uploadIndex = segments.indexOf("uploads");
  return uploadIndex === -1 ? "" : segments[uploadIndex + 1] ?? "";
}

export const mockCoachingServices = createCoachingServices(mockCoachingApi);
